import { useState, useEffect } from 'react';

// Tailwind breakpoints
const BREAKPOINTS = {
  sm: 640, // 640px (40rem) - mobile
  md: 768, // 768px (48rem) - tablet
  lg: 1024, // 1024px (64rem) - desktop
  xl: 1280, // 1280px (80rem) - large desktop
  '2xl': 1536, // 1536px (96rem) - extra large desktop
} as const;

interface UseBreakpointParams {
  breakpoint: number;
}

/**
 * Generic hook to check if viewport is below a specific breakpoint
 * @param params - Object containing the breakpoint in pixels
 * @returns true if viewport width is below the breakpoint, false otherwise
 */
function useBreakpoint({ breakpoint }: UseBreakpointParams) {
  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => {
      setIsBelowBreakpoint(window.innerWidth < breakpoint);
    };
    mql.addEventListener('change', onChange);
    setIsBelowBreakpoint(window.innerWidth < breakpoint);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpoint]);

  return !!isBelowBreakpoint;
}

// Specific hooks for Tailwind breakpoints
export const useIsMobile = () => useBreakpoint({ breakpoint: BREAKPOINTS.md });
export const useIsTablet = () => useBreakpoint({ breakpoint: BREAKPOINTS.lg });
export const useIsDesktop = () => useBreakpoint({ breakpoint: BREAKPOINTS.xl });
